import React, { createContext, useContext, useState, useCallback } from 'react';
import apiClient, { hasAuthToken } from '../services/apiClient';
import { useAuth } from '../auth/AuthContext';

const TimezoneContext = createContext();

const TIMEZONE_OVERRIDE_KEY = 'timezone_override';
const DEFAULT_TIMEZONE = 'UTC';

// Common timezones for the selector
export const COMMON_TIMEZONES = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'EST/EDT (New York)' },
  { value: 'America/Chicago', label: 'CST/CDT (Chicago)' },
  { value: 'America/Denver', label: 'MST/MDT (Denver)' },
  { value: 'America/Los_Angeles', label: 'PST/PDT (Los Angeles)' },
  { value: 'Europe/London', label: 'GMT/BST (London)' },
  { value: 'Europe/Paris', label: 'CET/CEST (Paris)' },
  { value: 'Asia/Tokyo', label: 'JST (Tokyo)' },
  { value: 'Asia/Hong_Kong', label: 'HKT (Hong Kong)' },
  { value: 'Asia/Singapore', label: 'SGT (Singapore)' },
  { value: 'Asia/Shanghai', label: 'CST (Shanghai)' },
  { value: 'Australia/Sydney', label: 'AEDT/AEST (Sydney)' },
];

export function TimezoneProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [overrideTimezone, setOverrideTimezone] = useState(() => {
    // Load override from localStorage on mount
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(TIMEZONE_OVERRIDE_KEY);
      return stored || null;
    }
    return null;
  });

  // Don't fetch preferences automatically on mount to avoid race condition with token
  // Preferences will be fetched on-demand when UserPreferences component mounts
  // Use a simple state to store preferences instead of SWR automatic fetching
  const [preferences, setPreferences] = useState(null);
  
  const mutatePreferences = (data) => {
    setPreferences(data);
  };

  // Save override to localStorage
  const setOverride = (timezone) => {
    if (timezone) {
      localStorage.setItem(TIMEZONE_OVERRIDE_KEY, timezone);
      setOverrideTimezone(timezone);
    } else {
      localStorage.removeItem(TIMEZONE_OVERRIDE_KEY);
      setOverrideTimezone(null);
    }
  };

  // Load user preferences on demand (called from UserPreferences component)
  // Use useCallback to stabilize the function reference and prevent infinite loops
  const loadUserPreferences = useCallback(async () => {
    if (!user || authLoading) {
      return { timezone: DEFAULT_TIMEZONE };
    }
    try {
      const response = await apiClient.get('/users/me/preferences');
      mutatePreferences(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to load user preferences:', error);
      return { timezone: DEFAULT_TIMEZONE };
    }
  }, [user, authLoading]);

  // Update user's default timezone preference
  const updateUserTimezone = async (timezone) => {
    try {
      await apiClient.put('/users/me/preferences', { timezone });
      await mutatePreferences({ timezone });
      // Clear override if setting default
      if (!overrideTimezone) {
        setOverride(null);
      }
    } catch (error) {
      console.error('Failed to update user timezone:', error);
      throw error;
    }
  };

  // Get the active timezone (override takes precedence, then user preference, then default)
  const activeTimezone = overrideTimezone || preferences?.timezone || DEFAULT_TIMEZONE;
  const userTimezone = preferences?.timezone || DEFAULT_TIMEZONE;

  const value = {
    activeTimezone,
    userTimezone,
    overrideTimezone,
    setOverride,
    updateUserTimezone,
    loadUserPreferences,
    clearOverride: () => setOverride(null),
    hasOverride: !!overrideTimezone,
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
}

export function useTimezone() {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within TimezoneProvider');
  }
  return context;
}

// Utility function to format timestamp with timezone
export function formatTimestampWithTimezone(timestamp, options = {}) {
  if (!timestamp) return null;
  
  const {
    timezone = null, // If not provided, will use activeTimezone from context
    includeSeconds = false,
    includeDate = true,
    includeTime = true,
    format = 'full', // 'full', 'date', 'time', 'relative'
  } = options;

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // Relative format for recent times
  if (format === 'relative') {
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
  }

  const formatOptions = {
    timeZone: timezone || 'UTC',
  };

  // Handle 'time' format - only time, no date
  if (format === 'time') {
    formatOptions.hour = '2-digit';
    formatOptions.minute = '2-digit';
    if (includeSeconds) {
      formatOptions.second = '2-digit';
    }
    formatOptions.hour12 = false;
    const formatted = date.toLocaleTimeString('en-US', formatOptions);
    // Don't add timezone label for time-only format
    return formatted;
  }

  if (includeDate) {
    formatOptions.month = 'short';
    formatOptions.day = 'numeric';
    formatOptions.year = 'numeric';
  }

  if (includeTime) {
    formatOptions.hour = '2-digit';
    formatOptions.minute = '2-digit';
    if (includeSeconds) {
      formatOptions.second = '2-digit';
    }
    formatOptions.hour12 = false;
  }

  let formatted = date.toLocaleString('en-US', formatOptions);
  
  // Add timezone label
  if (timezone && timezone !== 'UTC') {
    // Get timezone abbreviation
    const tzName = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value || timezone;
    formatted += ` ${tzName}`;
  } else {
    formatted += ' UTC';
  }

  return formatted;
}
